import { TableCell } from '@repo/go-table';
import { Tooltip } from '@repo/go-ui';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { DescriptionList } from '../../shared/components/DescriptionList/DescriptionList';
import type { TimeZonesStore } from '../../shared/stores/TimeZonesStore';
import type { TUserInfo } from '../types';

export const UserInfo = observer(
  ({
    updatedBy,
    createdBy,
    updatedAt,
    createdAt,
    timeZonesStore,
  }: {
    updatedBy?: TUserInfo | null;
    createdBy?: TUserInfo | null;
    updatedAt?: string | null;
    createdAt: string;
    timeZonesStore: TimeZonesStore;
  }) => {
    const { t } = useTranslation();
    const formatDate = (date: string) => format(timeZonesStore.getDate(new Date(date).valueOf()), 'dd.MM.yyyy HH:mm');
    const displayUser = updatedBy ?? createdBy;
    const items = [
      {
        term: t('alertsManagerWidget:authorDetails.createdBy'),
        value: createdBy ? formatUserNameFull(createdBy) : '-',
      },
      {
        term: t('alertsManagerWidget:authorDetails.createdAt'),
        value: createdAt ? formatDate(createdAt) : '-',
      },
    ];
    if (updatedBy && updatedAt) {
      items.push({
        term: t('alertsManagerWidget:authorDetails.updatedBy'),
        value: formatUserNameFull(updatedBy),
      });
      items.push({
        term: t('alertsManagerWidget:authorDetails.updatedAt'),
        value: updatedAt ? formatDate(updatedAt) : '-',
      });
    }

    return (
      <TableCell>
        <Tooltip title={<DescriptionList items={items} />}>
          <span>{displayUser ? formatUserNameShort(displayUser) : '-'}</span>
        </Tooltip>
      </TableCell>
    );
  },
);

export function formatUserNameShort(userData: TUserInfo): string {
  const lastName = userData.lastName?.trim();
  const firstName = userData.firstName?.trim();

  if (lastName && firstName) {
    const givenInitial = firstName ? `${firstName.charAt(0).toUpperCase()}.` : '';
    const parts = [lastName, givenInitial].filter(Boolean);
    return parts.join(' ');
  }

  if (lastName || firstName) {
    return lastName || firstName;
  }

  return userData.username;
}

export function formatUserNameFull(userData: TUserInfo): string {
  const lastName = userData.lastName?.trim();
  const firstName = userData.firstName?.trim();

  if (lastName || firstName) {
    const parts = [lastName, firstName].filter(Boolean);
    return parts.join(' ');
  }

  return userData.username;
}
